import React, { useCallback, useEffect, useRef, useState } from 'react';
import { t } from '@lingui/core/macro';

import { getApiUrl } from '../utils/api';
import { authFetch } from '../utils/authFetch';
import { useProject } from '../contexts/ProjectContext';
import { ReactComponent as UploadArrowIcon } from "../assets/upload-arrow.svg";
import AssetManagement, { AssetInfoBase, ResetFileInput } from './AssetManagement';
import { isProjectOwnedAssetUrl, isUserUploadedAssetUrl } from '../utils/assetOwnership';
import { trackEvent } from '../statsig';

type LogoSource = 'shared' | 'uploaded';

export interface LogoInfo extends AssetInfoBase {
  filename?: string;
  projectId?: string;
  source?: LogoSource;
}

interface LogoManagementProps {
  onLogoListChange: (logos: LogoInfo[]) => void;
  initialLogos?: LogoInfo[];
  onLogoUploaded?: (logoName: string, serverUrl: string) => void;
  onLogoDeleted?: (logoName: string) => void;
}

const LogoManagement: React.FC<LogoManagementProps> = ({ onLogoListChange, initialLogos = [], onLogoUploaded, onLogoDeleted }) => {
  const { selectedProject } = useProject();
  const selectedProjectId: string | undefined = selectedProject?.id;
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const availableLogosRef = useRef<LogoInfo[]>([]);
  const logosLoadingRef = useRef<Promise<LogoInfo[]> | null>(null);

  // Lazy load available logos only when needed
  const getAvailableLogos = useCallback(async (): Promise<LogoInfo[]> => {
    // Return cached result if already loaded
    if (availableLogosRef.current.length > 0) {
      return availableLogosRef.current;
    }

    // Return existing promise if already loading
    if (logosLoadingRef.current) {
      return logosLoadingRef.current;
    }

    // Start loading
    logosLoadingRef.current = (async () => {
      try {
        const response = await authFetch(`${getApiUrl()}/available-logos?projectId=${selectedProject?.id}`);
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

        const result = await response.json();
        if (result.status === 'OK') {
          const availableLogos: LogoInfo[] = result.files.map((logo: any) => ({
            name: logo.name,
            filename: typeof logo.filename === 'string' ? logo.filename : undefined,
            projectId: typeof logo.projectId === 'string' ? logo.projectId : undefined,
            source: logo.source === 'uploaded' ? 'uploaded' : 'shared',
            file: null, // No file object for server logos
            url: `${window.location.origin}${logo.url}`,
            enabled: false // Start with disabled
          }));

          // Cache the result
          availableLogosRef.current = availableLogos;
          return availableLogos;
        }
      } catch (err) {
        console.error('Failed to load available logos:', err);
      }

      return [];
    })();

    return logosLoadingRef.current;
  }, [selectedProject]);

  // Initialize state with lazy loading
  const [logos, setLogos] = useState<LogoInfo[]>(() => {
    // Start loading available logos in the background
    getAvailableLogos().then(availableLogos => {
      if (availableLogos.length > 0) {
        setLogos(prevLogos => {
          // If initialLogos was empty, populate with all available logos (enabled by default)
          if (prevLogos.length === 0 && initialLogos.length === 0) {
            const enabledAvailableLogos = availableLogos.map(logo => ({
              ...logo,
              enabled: true // Enable all available logos when no initial logos are provided
            }));
            // Notify parent component of the change
            setTimeout(() => onLogoListChange(enabledAvailableLogos), 0);
            return enabledAvailableLogos;
          }

          // Merge with existing logos, avoiding duplicates
          const existingNames = new Set(prevLogos.map(l => l.name.toLowerCase()));
          const newLogos = availableLogos.filter(l => !existingNames.has(l.name.toLowerCase()));

          if (newLogos.length > 0) {
            const updated = [...prevLogos, ...newLogos];
            // Notify parent component of the change
            setTimeout(() => onLogoListChange(updated), 0);
            return updated;
          }

          return prevLogos;
        });
      }
    });

    return initialLogos;
  });

  // Update when initialLogos changes
  useEffect(() => {
    if (initialLogos.length === 0) {
      // If initialLogos is empty, check if we should populate with available logos
      getAvailableLogos().then(availableLogos => {
        if (availableLogos.length > 0) {
          const enabledAvailableLogos = availableLogos.map(logo => ({
            ...logo,
            enabled: true // Enable all available logos when no initial logos are provided
          }));
          setLogos(enabledAvailableLogos);
          onLogoListChange(enabledAvailableLogos);
        } else {
          setLogos([]);
        }
      });
    } else {
      setLogos(initialLogos);
    }
  }, [initialLogos, getAvailableLogos, onLogoListChange]);

  const uploadLogoToAPI = async (file: File) => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await authFetch(`${getApiUrl()}/upload-logo?projectId=${selectedProject!.id}`, { method: 'POST', body: formData });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const result = await response.json();
      if (result.status === 'OK') {
        setError(null);
        const projectId: string = selectedProject!.id;
        const filename: string = encodeURIComponent(file.name);
        const cleanName: string = file.name.replace(/\.[^/.]+$/, '');
        const serverLogoUrl: string = `${window.location.origin}/users/${projectId}/logos/${filename}`;
        // Replace blob URL with final URL in local state, then notify parent
        setLogos(prev => {
          let found = false;
          const updated = prev.map(l => {
            if (l.name === cleanName) {
              found = true;
              return { ...l, url: serverLogoUrl, enabled: true, file: null, filename: file.name, projectId, source: 'uploaded' as const };
            }
            return l;
          });
          const next = found
            ? updated
            : [...updated, { name: cleanName, file: null, url: serverLogoUrl, enabled: true, filename: file.name, projectId, source: 'uploaded' as const }];
          setTimeout(() => onLogoListChange(next), 0);
          return next;
        });

        if (onLogoUploaded) {
          onLogoUploaded(cleanName, serverLogoUrl);
        }
        trackEvent('logo_uploaded', {
          project_id: selectedProject?.id,
          logo_name: cleanName,
        });
      } else {
        setError(result.message || 'Unknown error occurred');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload logo');
    } finally {
      setLoading(false);
    }
  };

  const handleFilesSelected = (files: File[], resetFileInput: ResetFileInput): void => {
    const existingNamesLower: Set<string> = new Set(logos.map((l) => l.name.toLowerCase()));
    const addedNamesLower: Set<string> = new Set<string>();
    const newLogos: LogoInfo[] = [];

    files.forEach((file: File) => {
      const logoName = file.name.replace(/\.[^/.]+$/, '');
      const nameLower = logoName.toLowerCase();

      if (existingNamesLower.has(nameLower) || addedNamesLower.has(nameLower)) {
        return;
      }

      addedNamesLower.add(nameLower);
      const url = URL.createObjectURL(file);
      // Send to backend
      uploadLogoToAPI(file);

      newLogos.push({ name: logoName, file, url, enabled: true, filename: file.name, projectId: selectedProject!.id, source: 'uploaded' });
    });

    if (newLogos.length > 0) {
      const updated = [...logos, ...newLogos];
      setLogos(updated);
      // Do not propagate to parent yet; wait for upload success to avoid blob URLs.
    }

    resetFileInput();
  };

  const handleDeleteLogo = async (logo: LogoInfo, idx: number): Promise<void> => {
    const logoFileName: string = logo.filename || (logo.url.split('/').pop() ?? ''); 
    const logoName: string = logo.name;
    const isProjectOwnedLogo: boolean =
      !!logo.file || isProjectOwnedAssetUrl(logo.url, selectedProjectId, 'logos');
    if (!window.confirm(t`Are you sure you want to delete the logo "${logoName}"?`)) {
      return;
    }

    try {
      if (isProjectOwnedLogo) {
        const response = await authFetch(
          `${getApiUrl()}/delete-logo?projectId=${selectedProject!.id}&logoName=${encodeURIComponent(logoFileName)}`,
          { method: 'DELETE' }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
      }

      // Remove from local state
      const updated = logos.filter((_, i) => i !== idx);
      setLogos(updated);
      onLogoListChange(updated);
      
      // Notify parent of deletion
      if (onLogoDeleted) {
        onLogoDeleted(logoName);
      }
      trackEvent('logo_deleted', {
        project_id: selectedProject?.id,
        logo_name: logoName,
        deleted_from_storage: isProjectOwnedLogo,
      });
    } catch (err) {
      console.error('Error deleting logo:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete logo');
    }
  };

  const isUserUploadedLogo = (logo: LogoInfo): boolean =>
    logo.source === 'uploaded' || !!logo.file || isUserUploadedAssetUrl(logo.url, 'logos');

  const systemDefaultsLabel: string = String(t`System Defaults`);
  const userUploadedLabel: string = String(t`User Uploaded`);

  return (
    <AssetManagement<LogoInfo>
      title={String(t`Logo Management`)}
      uploadTitle={String(t`Upload Logos`)}
      uploadTooltip={String(t`Supported formats: .png, .jpg, .jpeg, .svg, .webp`)}
      uploadAccept=".png,.jpg,.jpeg,.svg,.webp"
      uploadAriaLabel={String(t`Upload logos`)}
      uploadIcon={<UploadArrowIcon width={24} height={24} style={{ display: 'block' }} aria-hidden="true" focusable="false" />}
      onFilesSelected={handleFilesSelected}
      listLabel={String(t`Logo List`)}
      listTooltip={String(t`You can select logos in the sheet data by clicking the cell`)}
      searchPlaceholder={String(t`Search logos...`)}
      emptyText={String(t`No logos uploaded yet.`)}
      noMatchText={String(t`No logos match your search.`)}
      loading={loading}
      loadingText={String(t`Uploading logo...`)}
      error={error}
      assets={logos}
      onAssetsChange={(next) => {
        setLogos(next);
        onLogoListChange(next);
      }}
      getSectionLabel={(logo) =>
        isUserUploadedLogo(logo)
          ? userUploadedLabel
          : systemDefaultsLabel
      }
      sectionOrder={[systemDefaultsLabel, userUploadedLabel]}
      isDeletable={isUserUploadedLogo}
      deleteTitle={String(t`Delete logo`)}
      onDeleteAsset={(logo, idx) => {
        void handleDeleteLogo(logo, idx);
      }}
    />
  );
};

export default LogoManagement;
